import { assert_number_gte_0, assert_number_gte_1 } from '@shared/asserts/utils_asserts';
import { DTO__base } from '@shared/common/DTO__base';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { DTO__number_range } from '@shared/dto/generation/settings/DTO__number_range';
import { DTO__grid2d } from '@shared/dto/grid2d/DTO__grid2d';
import { DTO__grid2d_chunk } from '@shared/dto/grid2d_chunk/DTO__grid2d_chunk';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { DTO__grid2d_virtual_page } from './DTO__grid2d_virtual_page';

export class DTO__grid2d_virtual extends DTO__base {
  /*
   * STATIC FIELDS
   */
  static _class_name_static: string = ENUM__dto_class_name.GRID2D_VIRTUAL;

  /*
   * STATIC METHODS
   */

  static get_visible_cells_amount(viewport_width: number, total_columns: number, max_side: number, grid2d: DTO__grid2d) {
    let out = 1;
    if (viewport_width === 0) return out;

    const total_rows_new = Math.max(Math.ceil(viewport_width / max_side), 1);
    const total_images_on_screen = total_rows_new * total_columns;

    const { total_cells_amount } = grid2d;

    out = Math.min(total_cells_amount, total_images_on_screen);
    out = Math.max(out, 1);

    return out;
  }
  /*
   * INSTANCE FIELDS EXTERNAL
   */

  grid2d: DTO__grid2d;

  //
  cells__amount__visible: number;
  cells__amount__grid2d_total!: number;

  cell__to_load_index_absolute: number;

  //
  pages__to_load_absolute_indeces_set: Set<number>;

  page__index_absolute_max!: number; // = total pages amount - 1

  page__to_load_index_absolute!: number;

  pages__to_load!: DTO__grid2d_virtual_page[];

  //
  virtualizer_size!: number;
  total_columns: number;

  /*
   * INSTANCE FIELDS INTERNAL
   */

  ui_grid2d_cells_sort_newest_first: boolean;

  /*
   * METHODS
   */

  constructor() {
    super();

    this.grid2d = DTO__grid2d.CREATE_STUB();
    this.total_columns = CONSTANTS__shared.GRID2D_VIRTUAL_TOTAL_COLUMNS_DEFAULT;
    this.cells__amount__visible = CONSTANTS__shared.GRID2D_VIRTUAL_VISIBLE_CELLS_AMOUNT_DEFAULT;
    this.cell__to_load_index_absolute = CONSTANTS__shared.GRID2D_VIRTUAL_CELL_TO_RENDER_ABSOULTE_DEFAULT;
    this.ui_grid2d_cells_sort_newest_first = false;

    this.pages__to_load_absolute_indeces_set = new Set();

    this.cells__amount__grid2d_total = this.grid2d.total_cells_amount;
    this.page__index_absolute_max = 0;
    this.page__to_load_index_absolute = 0;

    this.pages__to_load = [];

    this.virtualizer_size = 0;
  }

  assert_standard_fields(): void {
    assert_number_gte_1(this.total_columns);
    assert_number_gte_1(this.cells__amount__visible);

    assert_number_gte_0(this.cell__to_load_index_absolute);

    assert_number_gte_0(this.page__index_absolute_max);
    assert_number_gte_0(this.page__to_load_index_absolute);

    assert_number_gte_0(this.virtualizer_size);
    assert_number_gte_0(this.cells__amount__grid2d_total);
  }

  init(grid2d_virtual_previous: DTO__grid2d_virtual) {
    this.try_merge_with_previous(grid2d_virtual_previous);

    if (this.grid2d.total_cells_amount > 0) {
      this.fields__init();

      this.page__init_to_load_before_current();
      this.page__init_to_load_after_current();

      this.pages__init_to_load();
    }
  }

  private try_merge_with_previous(grid2d_virtual_previous: DTO__grid2d_virtual) {
    if (grid2d_virtual_previous.grid2d._id !== this.grid2d._id) return;
    if (grid2d_virtual_previous.grid2d.entity_type !== this.grid2d.entity_type) return;
    if (grid2d_virtual_previous.cells__amount__visible !== this.cells__amount__visible) return;

    this.pages__to_load_absolute_indeces_set = new Set(grid2d_virtual_previous.pages__to_load_absolute_indeces_set);
  }

  private fields__init() {
    this.page__index_absolute_max = this.cells__amount__visible !== 0 ? Math.ceil(this.cells__amount__grid2d_total / this.cells__amount__visible) - 1 : 0;

    this.page__calculate_to_load_index_absolute();

    if (isNaN(this.page__to_load_index_absolute)) debugger;

    this.pages__to_load_absolute_indeces_set.add(this.page__to_load_index_absolute);
    this.pages__to_load_absolute_indeces_set = this.pages__to_load_absolute_indeces_set.sort_numbers();
  }

  private page__calculate_to_load_index_absolute() {
    let virtual_page_index = 0;

    const has_visible_cells = this.cells__amount__visible !== 0;
    const has_valid_cell_index = this.cell__to_load_index_absolute > 0 && this.cell__to_load_index_absolute < this.grid2d.total_cells_amount;

    if (has_visible_cells && has_valid_cell_index) {
      const exact_division = this.cell__to_load_index_absolute % this.cells__amount__visible === 0;

      virtual_page_index = exact_division
        ? this.cell__to_load_index_absolute / this.cells__amount__visible
        : Math.ceil(this.cell__to_load_index_absolute / this.cells__amount__visible) - 1;
    }

    this.page__to_load_index_absolute = virtual_page_index;
  }

  private page__init_to_load_before_current() {
    const page_to_render_index_previous_first = Math.max(this.page__to_load_index_absolute - 1, 0);
    const page_to_render_index_previous_second = Math.max(page_to_render_index_previous_first - 1, 0);

    this.pages__to_load_absolute_indeces_set.add(page_to_render_index_previous_first);
    this.pages__to_load_absolute_indeces_set.add(page_to_render_index_previous_second);
    this.pages__to_load_absolute_indeces_set = this.pages__to_load_absolute_indeces_set.sort_numbers();
  }

  private page__init_to_load_after_current() {
    let page_to_render_index_next_first = Math.min(this.page__to_load_index_absolute + 1, this.page__index_absolute_max);
    let page_to_render_index_next_second = Math.min(page_to_render_index_next_first + 1, this.page__index_absolute_max);

    this.pages__to_load_absolute_indeces_set.add(page_to_render_index_next_first);
    this.pages__to_load_absolute_indeces_set.add(page_to_render_index_next_second);
    this.pages__to_load_absolute_indeces_set = this.pages__to_load_absolute_indeces_set.sort_numbers();
  }

  private pages__init_to_load() {
    let pages_to_render_indeces_list = this.pages__to_load_absolute_indeces_set.to_array();

    for (let page_index of pages_to_render_indeces_list) {
      let page = new DTO__grid2d_virtual_page();
      page.page_index_absolute = page_index;

      const max_images_on_page = Math.min(this.cells__amount__visible, this.cells__amount__grid2d_total);

      const cells_indices_to_render_range_absolute_min = page_index * max_images_on_page;

      const range_absolute_max = Math.min(cells_indices_to_render_range_absolute_min + max_images_on_page, this.cells__amount__grid2d_total) - 1;
      const cells_indices_to_render_range_absolute_max = Math.max(0, range_absolute_max);

      page.cells_indices_to_load_range_absolute = new DTO__number_range(cells_indices_to_render_range_absolute_min, cells_indices_to_render_range_absolute_max);

      const { min, max } = page.cells_indices_to_load_range_absolute;

      let chunk_min = this.chunk__get_by_cell_index(min);
      let chunk_max = this.chunk__get_by_cell_index(max);

      if (this.ui_grid2d_cells_sort_newest_first) [chunk_min, chunk_max] = [chunk_max, chunk_min];

      // calc when we get cells_indices_to_render_range_absolute
      const chunks_to_load_indices_range = new DTO__number_range(chunk_min.chunk_index, chunk_max.chunk_index);

      for (let i = chunks_to_load_indices_range.min; i <= chunks_to_load_indices_range.max; i++) {
        const chunk_to_load_id = this.grid2d.chunk__get_id_by_index(i);

        page.chunks_to_load_ids.push(chunk_to_load_id);
      }

      this.pages__to_load.push(page);
    }
  }

  /*
   *
   */

  private chunk__get_by_cell_index(index_cell: number) {
    let index_cell_adjusted = this.ui_grid2d_cells_sort_newest_first ? this.grid2d.total_cells_amount - 1 - index_cell : index_cell;

    index_cell_adjusted = Math.max(0, index_cell_adjusted);

    return this.grid2d.chunk__get_by_cell_index(index_cell_adjusted);
  }

  chunk__get_all_to_load(): DTO__grid2d_chunk[] {
    const out: DTO__grid2d_chunk[] = [];

    const chunk_ids_set = new Set<string>();

    for (const page of this.pages__to_load) {
      for (const chunk_id of page.chunks_to_load_ids) {
        chunk_ids_set.add(chunk_id);
      }
    }

    const chunk_ids_list = chunk_ids_set.to_array();

    for (const chunk_id of chunk_ids_list) {
      const chunk = this.grid2d.chunk__get_by_id_or_assert(chunk_id);

      out.push(chunk);
    }

    return out;
  }
}
